import React from 'react';
import { ModalView, ModalViewProps } from '@cp/ds/src/components/modalView';
import { ApiServerEntityIDType } from '@cp/utils/apiServer/types';
import { useIsMobile } from '@cp/ds/src/hooks/useIsMobile';
import { CareerId } from '@common/const/catalog/career';
import { useModal } from 'src/shared/modalProvider';
import { CAREER_MODAL_NAME } from 'src/app/shared/components/modals/career/const';
import { IVacancyCreateOrEditHandle, VacancyCreateOrEditProps } from './model';
import { New, NewProps } from './new';
import { Existed } from './existed';

export * from './new';
export * from './model';

type IVacancyCreateOrEditState = {
  isOpened: boolean;
} & (
  | {
      initialValues: NewProps['initialValues'];
    }
  | {
      id: ApiServerEntityIDType;
    }
  | {}
);

const modalViewConfig: ModalViewProps['config'] = { mobile: 'fullscreen', desktop: 'drawerRight' };

export const VacancyCreateOrEdit = React.memo(
  React.forwardRef<IVacancyCreateOrEditHandle, VacancyCreateOrEditProps>((props, ref) => {
    const isMobile = useIsMobile();
    const { openModal } = useModal();
    const [state, setState] = React.useState<IVacancyCreateOrEditState>({ isOpened: false });

    React.useImperativeHandle(
      ref,
      () => ({
        edit: (id) => {
          setState({ isOpened: true, id });
        },
        create: async (initialValues: Partial<NewProps['initialValues']> = {}) => {
          try {
            const careerId =
              initialValues.careerId ??
              (await new Promise<CareerId>((resolve, reject) =>
                openModal(CAREER_MODAL_NAME, {
                  onModalDestroy: reject,
                  onCareerSelected: resolve,
                }),
              ));

            setState({
              isOpened: true,
              initialValues: {
                ...initialValues,
                careerId,
              },
            });
          } catch {}
        },
      }),
      [openModal],
    );

    const handleClose = React.useCallback(() => setState((prevState) => ({ ...prevState, isOpened: false })), []);

    return (
      <ModalView
        config={modalViewConfig}
        isOpen={state.isOpened}
        onClose={handleClose}
        showCloseButton={!isMobile || 'initialValues' in state}
      >
        {state.isOpened &&
          ('id' in state ? (
            <Existed id={state.id} onClose={handleClose} />
          ) : 'initialValues' in state ? (
            <New initialValues={state.initialValues} onGoBack={handleClose} />
          ) : null)}
      </ModalView>
    );
  }),
);

export default VacancyCreateOrEdit;
